import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import {
  LoginOAuthParams,
  PkceCode,
  SwapAuthForAccessTokens,
  SwapAuthForAccessTokensParams
} from '../../../models';
import { MultiFactorAuthenticationUtilsService } from '../../../utils/multi-factor-authentication-utils.service';
import { AddBackupMfaService } from '../../multi-factor-authentication-adapter-service/add-backup-mfa/add-backup-mfa.service';
import { CancelAuthenticationService } from '../../multi-factor-authentication-adapter-service/cancel-authentication/cancel-authentication.service';
import { CancelMethodPairingService } from '../../multi-factor-authentication-adapter-service/cancel-method-pairing/cancel-method-pairing.service';
import { ContinueAuthenticationService } from '../../multi-factor-authentication-adapter-service/continue-authentication/continue-authentication.service';
import { InitiateAuthenticationService } from '../../multi-factor-authentication-adapter-service/initiate-authentication/initiate-authentication.service';
import { InitiateSsoSessionService } from '../../multi-factor-authentication-adapter-service/initiate-sso-session/initiate-sso-session.service';
import { SubmitEmailOtpService } from '../../multi-factor-authentication-adapter-service/submit-email-otp/submit-email-otp.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';
import { SwapAccessTokenService } from '../../swap-access-token-service/swap-access-token-service';
import { FlyDeltaMethodService } from '../fly-delta-method/fly-delta-method.service';
import { SmsMethodService } from '../sms-method/sms-method.service';

@Injectable({
  providedIn: 'root'
})
export class ExistingUserMfaWrapperHelperService {
  flowId = '';
  applicationId = '';
  applicationName = '';
  pkceCode = {} as PkceCode;
  enrollmentFlow = false;

  initiateSsoSession = inject(InitiateSsoSessionService);
  initiateAuthentication = inject(InitiateAuthenticationService);
  submitEmailOtp = inject(SubmitEmailOtpService);
  continueAuthentication = inject(ContinueAuthenticationService);
  addBackupMfa = inject(AddBackupMfaService);
  smsMethod = inject(SmsMethodService);
  flyDeltaMethod = inject(FlyDeltaMethodService);
  swapAccessToken = inject(SwapAccessTokenService);
  cancelPairing = inject(CancelMethodPairingService);
  cancelAuthentication = inject(CancelAuthenticationService);
  multiFactorAuthenticationUtils = inject(
    MultiFactorAuthenticationUtilsService
  );

  mfaUtils = inject(MfaUtilService);

  finish(
    loginOAuthParams: LoginOAuthParams
  ): Observable<SwapAuthForAccessTokens> {
    if (this.checkMethodAuthCode()) {
      const swapAccessTokenObject: SwapAuthForAccessTokensParams = {
        authorizationCode:
          this.smsMethod.authCode || this.flyDeltaMethod.authCode,
        oauthConfig: loginOAuthParams.oauthConfig,
        pkcCodeGenerated: this.enrollmentFlow
          ? this.pkceCode
          : loginOAuthParams.pkceCode
      };

      return from(
        this.swapAccessToken.swapAuthForAccessTokens(
          swapAccessTokenObject,
          true
        )
      );
    }

    return this.cancelAuthentication
      .cancelAuthentication(loginOAuthParams.oauthConfig, this.flowId)
      .pipe(
        concatMap((initialResult) => {
          const swapAccessTokenObject: SwapAuthForAccessTokensParams = {
            authorizationCode: initialResult.authorizeResponse?.code,
            oauthConfig: loginOAuthParams.oauthConfig,
            pkcCodeGenerated: this.enrollmentFlow
              ? this.pkceCode
              : loginOAuthParams.pkceCode
          };

          return from(
            this.swapAccessToken.swapAuthForAccessTokens(
              swapAccessTokenObject,
              true
            )
          );
        })
      );
  }

  checkMethodAuthCode(): boolean {
    return !!this.smsMethod.authCode || !!this.flyDeltaMethod.authCode;
  }
}
